import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Alert, Loader, Badge, Select } from '@dreamtree-org/twreact-ui';
import { ChevronLeft, Github, ExternalLink } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useGitApp, errText } from './useGitApp';
import FileExplorer from './FileExplorer';
import GitDiff from './GitDiff';
import SourceControl from './SourceControl';
import PullRequests from './PullRequests';
import ActivityFeed from './ActivityFeed';
import TasksTab from './TasksTab';

const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

/**
 * Development › workspace for one Git App.
 *
 * The shell owns the two things every tab needs — the app row and the branch list — so a
 * tab switch doesn't refetch them, and the branch switcher stays consistent across tabs.
 *
 * Branch state lives here rather than in each tab: switching branch in the File Explorer
 * and then opening Diff should compare the branch you were just looking at.
 */
export default function GitAppWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchApi } = useAppApi();
  const { act } = useGitApp(id);

  const [app, setApp] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('files');

  const loadApp = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('gitApps', { action: 'show', where: { id } });
      const row = Array.isArray(res) ? res[0] : (res?.data?.[0] || res?.data || res);
      if (!row || !row.id) throw new Error('App not found');
      setApp(row);
      setBranch((b) => b || row.branch || '');
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load this app'));
    } finally {
      setLoading(false);
    }
  }, [fetchApi, id]);

  useEffect(() => { loadApp(); }, [loadApp]);

  // Branches come from GitHub, so this is deliberately separate from the app row: a
  // failure here (no connection, rate limit) must not blank the whole workspace.
  useEffect(() => {
    let alive = true;
    act('branches')
      .then((d) => { if (alive) setBranches(d.branches || []); })
      .catch(() => { if (alive) setBranches([]); });
    return () => { alive = false; };
  }, [act]);

  if (loading) return <Loader text="Loading workspace…" />;
  if (err && !app) {
    return (
      <div className="space-y-4">
        <Alert variant="error" title="Workspace">{err}</Alert>
        <Button variant="secondary" leftIcon={<ChevronLeft size={16} />} onClick={() => navigate('/git-apps')}>Back to Git Apps</Button>
      </div>
    );
  }

  const repoUrl = app?.repo ? `https://github.com/${app.repo}` : null;

  const tabs = [
    { key: 'files', label: 'Files', content: <FileExplorer appId={id} branch={branch} /> },
    { key: 'source', label: 'Source control', content: <SourceControl appId={id} /> },
    { key: 'diff', label: 'Diff', content: <GitDiff appId={id} branches={branches} branch={branch} onOpenedPr={() => setTab('pulls')} /> },
    { key: 'pulls', label: 'Pull requests', content: <PullRequests appId={id} /> },
    { key: 'tasks', label: 'Tickets', content: <TasksTab appId={id} /> },
    { key: 'activity', label: 'Activity', content: <ActivityFeed appId={id} /> },
  ];
  const active = tabs.find((t) => t.key === tab) || tabs[0];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="secondary" size="sm" onClick={() => navigate('/git-apps')} aria-label="Back">
            <ChevronLeft size={18} />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold truncate">{app?.name}</h1>
            <div className="flex items-center gap-3 text-xs opacity-70 flex-wrap">
              <span className="flex items-center gap-1 font-mono"><Github size={12} /> {app?.repo}</span>
              {repoUrl ? (
                <a href={repoUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary-600 hover:underline">
                  <ExternalLink size={12} /> Open on GitHub
                </a>
              ) : null}
              {app?.last_ingested_at ? <span>ingested {new Date(app.last_ingested_at).toLocaleString()}</span> : <Badge variant="secondary">never ingested</Badge>}
            </div>
          </div>
        </div>

        <div className="w-64">
          {/* Drives the Files and Diff tabs. Newest-committed branch first, per the model. */}
          <Select
            label="Branch"
            value={branch}
            options={branches.map((b) => ({ value: b.name, label: b.name }))}
            onChange={(v) => setBranch(selVal(v))}
            placeholder={branches.length ? 'Repository default' : 'No branches loaded'}
            searchable
          />
        </div>
      </div>

      {err ? <Alert variant="error" title="Workspace">{err}</Alert> : null}

      {/* Only the ACTIVE pane is mounted. Each tab fetches on mount, so rendering all six
          would fire six GitHub round-trips on every workspace open. */}
      <div className="flex items-center gap-1 flex-wrap border-b border-secondary-200 dark:border-secondary-700 pb-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${tab === t.key ? 'bg-primary-50 text-primary-700' : 'opacity-60 hover:opacity-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {active.content}
    </div>
  );
}
